/**
 * Bot Detection Service (client-side only).
 *
 * Detects demo vs production from bot ID prefix: bot-demo- → demo/public DB,
 * otherwise production/baf. No APIM or backend call; used to avoid unnecessary
 * lookups in bot/resource management UI.
 */

import logger from 'utils/logger';

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

class BotDetectionService {
  constructor() {
    this.cache = new Map();
  }

  /**
   * Detect bot type from ID prefix (bot-demo- → demo/public).
   * @param {string} botId - Bot ID (e.g. bot-demo-abc123 or bot-op-xyz)
   * @param {boolean} useCache - Use cached result (default true)
   * @returns {Promise<{ success: boolean, bot_type: string, is_demo: boolean, database_type: string, config: object }>}
   */
  async detectBotType(botId, useCache = true) {
    if (!botId) {
      throw new Error('Bot ID is required');
    }

    if (useCache && this.cache.has(botId)) {
      const cached = this.cache.get(botId);
      if (Date.now() - cached.timestamp < CACHE_TTL_MS) {
        logger.debug('BOT-DETECTION', 'Cache hit', { botId, botType: cached.data.bot_type });
        return cached.data;
      }
    }

    const isDemo = typeof botId === 'string' && botId.startsWith('bot-demo-');
    const botType = isDemo ? 'demo' : 'production';
    const databaseType = isDemo ? 'public' : 'baf';
    const result = {
      success: true,
      bot_type: botType,
      is_demo: isDemo,
      database_type: databaseType,
      config: { is_demo: isDemo, bot_type: botType }
    };

    this.cache.set(botId, { data: result, timestamp: Date.now() });
    logger.debug('BOT-DETECTION', 'Detected', { botId, botType, databaseType });
    return result;
  }

  getCachedBotType(botId) {
    if (!this.cache.has(botId)) return null;
    const cached = this.cache.get(botId);
    if (Date.now() - cached.timestamp >= CACHE_TTL_MS) {
      this.cache.delete(botId);
      return null;
    }
    return cached.data;
  }

  clearCache(botId = null) {
    if (botId) this.cache.delete(botId);
    else this.cache.clear();
  }
}

const botDetectionService = new BotDetectionService();
export default botDetectionService;
